/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { get, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { spacing } from '@cash-design-system/react'
import { SubmitFormRequestElementResult } from '@cash-web/protos/squareup/franklin/app/submit_form.pb'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import {
  FormBlocker as FormBlockerProps,
  FormBlockerElement as FormBlockerElementProps,
  FormBlockerElementButtonElementStyle,
  FormBlockerElementButtonElementType,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import { getAppStoreUrl } from '@cash-web/shared-util-url'
import { isMobileDeviceOrTablet } from '@cash-web/shared-util-user-agent'
import { FlowsInteractSubmit } from '@squareup/customer-data-events-web'

import { getRequiredInputKeys } from '../../util'
import { getRequiredCheckboxElementKeys } from '../../util/checkBoxElement'
import { isUnsupportedActionId } from '../../util/isUnsupportedBlockerId'
import { setDefaultFormBlockerValues } from '../../util/setDefaultFormBlockerValues'
import { validateInputGroupHasSelection } from '../../util/validateInputGroupHasSelection'
import { BaseBlocker } from '../BaseBlocker/BaseBlocker'
import { BlockerRedirectToMobile } from '../BlockerRedirectToMobile/BlockerRedirectToMobile'
import { ButtonElement } from './elements/ButtonElement/ButtonElement'
import { CollapsibleSection } from './elements/CollapsibleSection/CollapsibleSection'
import { getCollapsibleSectionRows } from './elements/CollapsibleSection/getCollapsibleSectionRows'
import {
  FormBlockerElement,
  FormBlockerElementConfig,
  implementedElements,
} from './elements/FormBlockerElement/FormBlockerElement'
import { FormBlockerElementDecorator } from './elements/FormBlockerElementDecorator/FormBlockerElementDecorator'
import SpacerElement from './elements/SpacerElement/SpacerElement'
import { FormBlockerContext, FormBlockerContextProps } from './FormBlockerContext'
import { formBlockerElementSpacing } from './formBlockerElementSpacing'
import { handleFormAction } from './handleFormAction'
import { removeUnsupportedActions } from './removeUnsupportedActions'

export const MAX_ROW_BUTTON_TEXT_LENGTH = 12

const SectionStack = styled.div<{ id?: string }>`
  display: grid;
  row-gap: ${spacing.xl};
  ${props => {
    switch (props.id) {
      case 'AcceptTaxTermsOfService':
        return css`
          row-gap: ${spacing.xs};
        `
      default:
        return ''
    }
  }}
`

const formBlockerOverrides = {
  web_upsell_mobile_app: {
    remote_image_element: {
      button_element: {
        style: FormBlockerElementButtonElementStyle.SECONDARY,
        action: {
          text: 'Download Cash App',
          open_url_action: {
            url: getAppStoreUrl(),
          },
        },
      },
    } satisfies FormBlockerElementProps,
  },
}

// background_style is a styling modifier on the element wrapper, not an element payload.
export type FormBlockerElementType = keyof Omit<FormBlockerElementProps, 'background_style' | 'id'>

// If the currentElIndex is the last element, the next element will be `undefined`.
function getNextElementType(elements: FormBlockerElementProps[], currentElIndex: number) {
  const {
    background_style: nextBackgroundStyle,
    id: nextId,
    ...nextNonIdElementType
  } = elements[currentElIndex + 1] || {}

  const nextElementType = Object.keys(nextNonIdElementType)[0] as FormBlockerElementType | undefined
  return nextElementType
}

export function FormBlockerElements({ blocker }: { blocker: FormBlockerProps }) {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)
  let hasFocusedFirstElement = false
  const elements = blocker.elements ?? []
  const hasActions = blocker.primary_action || blocker.secondary_action
  const blockerElements: React.ReactNode[] = []

  for (let formSectionIndex = 0; formSectionIndex < elements.length; formSectionIndex += 1) {
    const element = elements[formSectionIndex]
    const { background_style, id, ...nonIdElementType } = element

    const elementType = Object.keys(nonIdElementType)[0] as FormBlockerElementType

    const collapsibleRows = getCollapsibleSectionRows(elements, formSectionIndex)
    const lastIndex = formSectionIndex + Math.max(collapsibleRows.length - 1, 0)

    const nextElementType = getNextElementType(elements, lastIndex)
    const spacing = nextElementType
      ? formBlockerElementSpacing(elementType, nextElementType)
      : hasActions
        ? formBlockerElementSpacing(elementType, 'actions')
        : 0

    if (collapsibleRows.length > 0) {
      blockerElements.push(
        <React.Fragment key={`formSection-${formSectionIndex}`}>
          <CollapsibleSection
            rows={collapsibleRows}
            expandedRowId={expandedRowId}
            onExpandedRowChange={setExpandedRowId}
          />
          {spacing > 0 && <SpacerElement proportion={spacing} />}
        </React.Fragment>
      )
      formSectionIndex = lastIndex
      continue
    }

    // The first focusable element in a form blocker should be focused
    const shouldAutoFocus =
      implementedElements[elementType as keyof FormBlockerElementConfig]?.isFocusable && !hasFocusedFirstElement
    if (shouldAutoFocus) {
      hasFocusedFirstElement = true
    }

    blockerElements.push(
      // TODO: fix the key if forms can be dynamic
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={`formSection-${formSectionIndex}`}>
        <FormBlockerElementDecorator index={formSectionIndex} elements={elements}>
          <FormBlockerElement element={element} formSectionIndex={formSectionIndex} shouldAutoFocus={shouldAutoFocus} />
        </FormBlockerElementDecorator>
        {spacing > 0 && <SpacerElement proportion={spacing} />}
      </React.Fragment>
    )
  }

  return <div>{blockerElements}</div>
}

type FormBlockerComponentProps = {
  id?: string
  blocker: FormBlockerProps
}

export function FormBlocker({ id, blocker }: FormBlockerComponentProps) {
  const journey = useJourneyContext()
  const [showUnsupportedScreen, setShowUnsupportedScreen] = useState(false)
  const form = useForm({
    defaultValues: setDefaultFormBlockerValues(blocker),
  })
  const { setError, clearError } = useContext(ErrorContext)
  const flow = useFlowContext()
  const selectedAction = useRef('')
  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const shouldUseOverride = isMobileDeviceOrTablet()
  if (shouldUseOverride) {
    const override = formBlockerOverrides[id as keyof typeof formBlockerOverrides]
    if (override && blocker.elements) {
      blocker.elements = blocker.elements?.map(element => {
        const key = Object.keys(element)[0] as keyof FormBlockerElementProps
        if (override[key as keyof typeof override]) {
          return { id, ...override[key as keyof typeof override] }
        }
        return element
      })
    }
  }

  const onFormBlockerAction = useCallback(
    async (action: BlockerAction) => {
      // If this action ID is unsupported, show redirect screen
      if (isUnsupportedActionId(action.submit_action?.id)) {
        setShowUnsupportedScreen(true)
        return
      }

      // Create a wrapper function to handle the return type difference between onBlockerAction and executeBlockerAction
      const onBlockerAction = async (action: BlockerAction, results?: SubmitFormRequestElementResult[]) => {
        const response = await flow.executeBlockerAction(action, results)
        return response || null
      }

      const { onFormResponse, onSubmit } = await handleFormAction(action, onBlockerAction, setError, clearError)

      if (action.submit_action) {
        try {
          clearError()

          // Double-check: If this action ID is unsupported, do not submit
          if (isUnsupportedActionId(action.submit_action.id)) {
            return
          }

          await form.handleSubmit(data => onSubmit(data))()
        } catch {
          logCdfEvent(
            FlowsInteractSubmit({
              blockerId: id || '',
              flowToken: flow.active?.flowToken || '',
              rootFlowToken: flow.active?.flowToken || '',
              blockerSubmitResult: 'FAILED',
            })
          )
          setError(
            t('Something went wrong. Please try again.', {
              context: 'Error message in form modal',
            })
          )
        }
      } else {
        const response = await onBlockerAction(action)
        onFormResponse(response)
      }
    },
    [flow, setError, clearError, form, logCdfEvent, id, t, setShowUnsupportedScreen]
  )

  const context: FormBlockerContextProps = useMemo(() => {
    return {
      blocker,
      onFormBlockerAction,
    }
  }, [blocker, onFormBlockerAction])

  const sectionStack = useMemo(() => {
    return ({ children }: { children?: ReactNode }) => <SectionStack id={id}>{children}</SectionStack>
  }, [id])

  const requiredKeys = [
    ...getRequiredInputKeys(blocker.elements ?? []),
    ...getRequiredCheckboxElementKeys(blocker.elements ?? []),
  ]

  const isEveryRequiredFieldDirtyAndValid = requiredKeys.every(requiredKey => {
    return (
      (get(form.formState.dirtyFields, requiredKey) || !!form.getValues(requiredKey)) &&
      !get(form.formState.errors, requiredKey)
    )
  })

  const formValues = form.watch()

  const everyRequiredFormGroupHasSelection = validateInputGroupHasSelection(blocker.elements ?? [], formValues)

  const isFormValid = isEveryRequiredFieldDirtyAndValid && everyRequiredFormGroupHasSelection
  const isSubmitting = form?.formState?.isSubmitting

  const cleanFormBlocker = removeUnsupportedActions(blocker, journey)

  const buttons = []
  if (cleanFormBlocker.secondary_action) {
    const canSecondaryButtonSubmit =
      !!cleanFormBlocker.secondary_action.submit_action && !cleanFormBlocker.primary_action?.submit_action

    const isSecondaryButtonDisabled = canSecondaryButtonSubmit ? !isFormValid || isSubmitting : isSubmitting

    buttons.push(
      <ButtonElement
        key="secondary"
        selectedAction={selectedAction}
        canSubmit={canSecondaryButtonSubmit}
        action={cleanFormBlocker.secondary_action}
        disabled={isSecondaryButtonDisabled}
        style={cleanFormBlocker.secondary_action_style || FormBlockerElementButtonElementStyle.SECONDARY}
      />
    )
  }

  if (cleanFormBlocker.primary_action) {
    const canPrimaryButtonSubmit = !!cleanFormBlocker.primary_action.submit_action
    const isPrimaryButtonDisabled = canPrimaryButtonSubmit ? !isFormValid || isSubmitting : isSubmitting

    buttons.push(
      <ButtonElement
        action={cleanFormBlocker.primary_action}
        canSubmit={!!cleanFormBlocker.primary_action.submit_action}
        key="primary"
        selectedAction={selectedAction}
        disabled={isPrimaryButtonDisabled}
        type={cleanFormBlocker.primary_action_type ?? FormBlockerElementButtonElementType.NORMAL}
        style={cleanFormBlocker.primary_action_style ?? FormBlockerElementButtonElementStyle.PRIMARY}
      />
    )
  }

  const shouldStackButtons =
    (cleanFormBlocker.secondary_action?.text &&
      cleanFormBlocker.primary_action?.text &&
      (cleanFormBlocker.secondary_action?.text.length > MAX_ROW_BUTTON_TEXT_LENGTH ||
        cleanFormBlocker.primary_action?.text?.length > MAX_ROW_BUTTON_TEXT_LENGTH)) ||
    false

  // If user clicked an unsupported action, show the redirect to mobile screen
  if (showUnsupportedScreen) {
    return <BlockerRedirectToMobile />
  }

  return (
    <FormBlockerContext.Provider value={context}>
      <BaseBlocker
        form={form}
        sectionStack={sectionStack}
        onSubmit={async () => {
          if (selectedAction.current === 'prominent' && cleanFormBlocker.primary_action) {
            return await context.onFormBlockerAction(cleanFormBlocker.primary_action)
          }
          if (selectedAction.current === 'standard' && cleanFormBlocker.secondary_action) {
            return await context.onFormBlockerAction(cleanFormBlocker.secondary_action)
          }
        }}
        buttons={buttons}
        disclaimerText={cleanFormBlocker.disclaimer_text}
        shouldStackButtons={shouldStackButtons}
      >
        {/* TODO: replace Stack with SpacerElement */}

        <FormBlockerElements blocker={cleanFormBlocker} />
      </BaseBlocker>
    </FormBlockerContext.Provider>
  )
}
